import * as React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { ExternalLink, Link, Lock, Monitor, User, X, Zap } from 'lucide-react'
import { toast } from 'sonner'
import { useDataTable } from '@/components/data-table/data-table-provider'
import { useSheetRowNavigation } from '@/hooks/useSheetRowNavigation'
import { useIsGlobalAdmin } from '@/hooks/useIsGlobalAdmin'
import { useRubricAnalysisAccess } from '@/hooks/useRubricAnalysisAccess'
import { useCapability } from '@/features/permissions/hooks'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { formatSheetTimestamp } from '@/lib/format'
import { normalizeFacts } from '@/features/calls/utils/factsUtils'
import { deriveTransferInfo } from '@/features/calls/utils/transferInfo'
import type { CallRecord } from '../types'
import { useCallDetail } from '@/features/calls/useCallDetail'
import { useCallSessions } from '@/features/calls/hooks/useCallSessions'
import { CallTextsSection } from './CallTextsSection'
import { CallFeedbackField } from './CallFeedbackField'
import { CallLabelBadge } from './CallLabelBadge'
import { CallLogsRecording } from './CallLogsRecording'
import { CallRecordingDownloadButton } from './CallRecordingDownloadButton'
import { CallTransferSection } from './CallTransferSection'
import { CallTypeBadge } from './CallTypeBadge'
import { CallLogsSheetFacts } from './CallLogsSheetFacts'
import { RubricAnalysisPanel } from './RubricAnalysisPanel'
import { TestCallFeedbackTimeline } from './TestCallFeedbackTimeline'
import { SheetSectionCard } from '@/components/SheetSectionCard'
import { SheetRowNavButtons } from '@/components/SheetRowNavButtons'
import { WaypointRow } from './WaypointRow'
import { AccessLock } from '@/components/AccessLock'
import { patientDetailsHref } from '@/features/patients/components/patientsTableSchema'

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-right">{value ?? '-'}</span>
    </div>
  )
}

function CallLogsSheetTitleRow({ call }: { call: CallRecord }) {
  return (
    <>
      <span className="text-primary-high-contrast whitespace-nowrap text-lg font-semibold">
        {formatSheetTimestamp(call.created_at)}
      </span>
      <CallTypeBadge type={call.type} />
    </>
  )
}

/**
 * Truncated subtitle value that reveals its full text in a tooltip. When `to`
 * is set the value becomes a navigation link, and the link itself is the
 * tooltip trigger — nesting a separate focusable trigger inside the link would
 * add a dead second tab stop.
 */
function SubtitleValue({
  value,
  className,
  to,
  state,
}: {
  value: string
  className?: string
  to?: string
  state?: Record<string, unknown>
}) {
  const trigger = to ? (
    <RouterLink
      to={to}
      state={state}
      className={cn(
        'hover:text-primary-high-contrast flex min-w-0 truncate hover:underline',
        className
      )}>
      {value}
    </RouterLink>
  ) : (
    <span tabIndex={0} className={cn('truncate', className)}>
      {value}
    </span>
  )
  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent>{value}</TooltipContent>
    </Tooltip>
  )
}

function CallLogsSheetSubtitleRow({ call }: { call: CallRecord }) {
  const patientName = call.patient_name?.trim()
  return (
    <TooltipProvider>
      <span className="text-neutral-low-contrast flex min-w-0 items-center gap-1 font-mono">
        <SubtitleValue value={call.call_id} className="max-w-[10ch]" />
        <CopyButton
          content={call.call_id}
          label="Copy Call ID"
          onCopy={() => toast.success('Call ID copied to clipboard')}
          className="hover:text-primary-high-contrast shrink-0 [&_svg]:size-3.5"
        />
      </span>
      <span className="flex min-w-0 items-center gap-1">
        <User className="h-3.5 w-3.5" aria-hidden />
        {patientName ? (
          <SubtitleValue
            value={patientName}
            // Deep link opens the Clients table narrowed to this client with its
            // details sheet open. `patients.id` is namespaced (the patients table
            // opts into namespaceUrlKeys); falls back to plain text with no id.
            to={
              call.patient_id ? patientDetailsHref(call.patient_id) : undefined
            }
          />
        ) : (
          <span className="truncate">N/A</span>
        )}
      </span>
      {call.duty_name && (
        <span className="flex min-w-0 items-center gap-1">
          <Zap className="h-3.5 w-3.5" aria-hidden />
          <SubtitleValue
            value={call.duty_name}
            to={call.duty_id ? `/duties/${call.duty_id}` : undefined}
            // dutyName state gives the duty tab its real title immediately
            // (see parseDutyNavState in config/mainRoutes.tsx).
            state={call.duty_id ? { dutyName: call.duty_name } : undefined}
          />
        </span>
      )}
    </TooltipProvider>
  )
}

/**
 * Body placeholder shown while a deep-linked row's data is still loading — the
 * sheet opens immediately from the URL selection, but the filtered table rows
 * arrive a beat later, so without this the body would flash an empty state.
 */
function CallLogsSheetBodySkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-2 rounded-md border p-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-16 w-full" />
        </div>
      ))}
    </div>
  )
}

/**
 * Top-level Calls sheet — reads the selected row from the DataTable context
 * (this component renders inside DataTableProvider via the `sheetSlot` prop)
 * and renders the v3 sheet:
 *   * Title row: timestamp + Inbound/Outbound badge + duration
 *   * Subtitle row: call_id (mono, truncated) · patient name · duty name
 *   * Body: call details content
 * Falls back to "Call Details" while no row is selected.
 */
export function CallLogsSheetWrapper() {
  const { table, rowSelection, isLoading } = useDataTable<CallRecord, unknown>()
  const isGlobalAdmin = useIsGlobalAdmin()
  const rubricAnalysis = useRubricAnalysisAccess()
  const showWaypoints = useCapability('calls.waypoints') === true
  const showMessages = useCapability('messages.list') === true
  const { toTask } = useAppNavigate()

  const {
    selectedRowKey,
    selectedRow,
    prevId,
    nextId,
    onPrev,
    onNext,
    onOpenChange,
  } = useSheetRowNavigation(table, rowSelection, !!isLoading)

  // Fetch call detail (for facts + future fields).
  const {
    callDetail,
    isLoading: isCallDetailLoading,
    error: callDetailError,
    refetch: refetchCallDetail,
    recordingType,
    resolvedRecordingUrl,
    isRecordingUrlLoading,
    recordingBlocked,
    isSupabaseRecording,
    resolveRecordingUrl,
    prefetchRecordingUrl,
    invalidateRecordingUrl,
    downloadRecording,
  } = useCallDetail(selectedRow?.original?.call_id ?? null, 'supabase')

  // Computer-use session(s) spawned from this call, if any.
  const { sessions: computerUseSessions } = useCallSessions(
    selectedRow?.original?.call_id ?? null
  )

  const call = selectedRow?.original

  const handleCopyCallLink = React.useCallback(() => {
    const callId = selectedRow?.original?.call_id
    if (!callId) return
    const params = new URLSearchParams(window.location.search)
    params.set('call_id', callId)
    params.set('details', '1')
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`
    if (!navigator.clipboard) {
      toast.error('Failed to copy link')
      return
    }
    navigator.clipboard.writeText(url).then(
      () => toast.success('Link to this call copied'),
      () => toast.error('Failed to copy link')
    )
  }, [selectedRow])

  const title = call ? <CallLogsSheetTitleRow call={call} /> : 'Call Details'
  const subtitle = call ? <CallLogsSheetSubtitleRow call={call} /> : null

  // Transfer info comes from the detail payload only — computing from the
  // (ended_reason-only) row while the detail loads would flash the wrong state.
  const transferInfo = callDetail ? deriveTransferInfo(callDetail) : null

  const factsCount = callDetail?.facts
    ? normalizeFacts(callDetail.facts).length
    : null
  const factsTitle =
    factsCount && factsCount > 0 ? `Captured Facts - ${factsCount}` : 'Facts'

  // The detail payload is the authority on whether audio exists — the list row's
  // `audio_url` comes from the Vapi blob alone, so it is null for a call served
  // from Storage. The row still seeds the first paint, before the detail lands,
  // so a call with no recording does not flash a player on its way to the empty
  // state (unanswered calls are the common case here).
  const hasRecording = callDetail
    ? Boolean(callDetail.audio_url)
    : Boolean(call?.audio_url)
  const seekRef = React.useRef<((timeSeconds: number) => void) | null>(null)

  React.useEffect(() => {
    // `recordingType` already implies the payload carried a URL, so it subsumes
    // the has-recording check this used to make against the list row.
    if (!recordingType || resolvedRecordingUrl) return
    void prefetchRecordingUrl(recordingType)
  }, [prefetchRecordingUrl, recordingType, resolvedRecordingUrl])

  return (
    // While the query is still loading (deep-link case: URL selects a row
    // before the filtered rows arrive) keep the sheet open on `isLoading`
    // alone — the row may still resolve. Once loading settles, require
    // `selectedRow` to actually exist: if the list query errored (or the
    // row was filtered out), `data` never gets it, and without this guard
    // the sheet stayed open indefinitely showing a blank "Call Details"
    // body over the error banner.
    <Sheet
      open={!!selectedRowKey && (isLoading || !!selectedRow)}
      onOpenChange={onOpenChange}>
      <SheetContent
        // Manual focus restore handled on close.
        onCloseAutoFocus={(e) => e.preventDefault()}
        // Avoid focus-on-open jump during slide-in.
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Full-width within container; rounded on inner edge.
        // Boundary shadow at the sheet-↔-chat seam lives on the chat sidebar
        // (it casts leftward onto the sheet — see ConversationSidebar.tsx),
        // because the chat is z-stacked above the sheet and any rightward
        // drop shadow from the sheet would be covered by the chat's opaque
        // bg. We just keep the default v3 --shadow-lg here for general
        // elevation over the table.
        className="bg-neutral-ui-bg overflow-y-auto gap-0 p-0 w-full max-w-none sm:max-w-lg rounded-l-xl"
        showCloseButton={false}>
        {/* sticky + bg matches SheetContent so the title row, call-context
            row, and Close (X) stay pinned while the body scrolls. Without
            this, scrolling through a multi-section call (Recording +
            Metadata + Feedback + Facts + admin sections) rolls the X
            off-screen, leaving only ESC as a discoverable close path. */}
        <SheetHeader className="bg-neutral-ui-bg sticky top-0 z-10">
          <div className="flex items-start justify-between gap-2">
            <SheetTitle className="flex min-w-0 flex-1 items-center gap-2 text-left">
              {isLoading && !selectedRowKey ? (
                <Skeleton className="h-7 w-36" />
              ) : (
                title
              )}
            </SheetTitle>
            <SheetRowNavButtons
              navigation={{ prevId, nextId, onPrev, onNext }}
              noun="call"
            />

            <SheetClose asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </SheetClose>
          </div>
          {subtitle ? (
            <div className="text-neutral-low-contrast flex flex-nowrap items-center gap-x-3 overflow-hidden text-sm">
              {subtitle}
            </div>
          ) : null}
        </SheetHeader>

        <SheetDescription className="sr-only">
          Selected row details
        </SheetDescription>

        <div className="px-4 pt-0 pb-4">
          {call ? (
            <div className="space-y-4">
              <SheetSectionCard
                title={
                  <span className="flex items-center gap-1.5">
                    Recording
                    <CallRecordingDownloadButton
                      hasRecording={hasRecording}
                      recordingType={recordingType}
                      recordingBlocked={recordingBlocked}
                      downloadRecording={downloadRecording}
                    />
                  </span>
                }
                body={
                  <CallLogsRecording
                    call={call}
                    messages={callDetail?.messages ?? null}
                    hasRecording={hasRecording}
                    hideTranscriptTabs={isSupabaseRecording}
                    resolvedRecordingUrl={resolvedRecordingUrl}
                    resolveRecordingUrl={resolveRecordingUrl}
                    recordingType={recordingType}
                    isRecordingUrlLoading={isRecordingUrlLoading}
                    onResolvedRecordingUrlError={
                      recordingType
                        ? () => invalidateRecordingUrl(recordingType)
                        : undefined
                    }
                    recordingBlocked={recordingBlocked}
                    onRetryDetail={() => void refetchCallDetail()}
                    seekRef={seekRef}
                  />
                }
              />

              {showMessages && (
                <CallTextsSection key={call.call_id} callId={call.call_id} />
              )}

              <SheetSectionCard
                title="Metadata"
                body={
                  <div className="divide-y rounded-md border px-3">
                    <MetaRow label="Ended Reason" value={call.ended_reason} />
                    <MetaRow
                      label="Label"
                      value={
                        <CallLabelBadge
                          label={call.label}
                          category={call.category}
                        />
                      }
                    />
                  </div>
                }
              />

              {transferInfo && (
                <SheetSectionCard
                  title="Transfer"
                  body={<CallTransferSection transfer={transferInfo} />}
                />
              )}

              {!isCallDetailLoading && (callDetail || callDetailError) && (
                <SheetSectionCard
                  // CallFeedbackField renders its own title row so the save
                  // status can sit on the same line, top-right.
                  title={callDetail ? undefined : 'Call Feedback'}
                  body={
                    callDetail ? (
                      <>
                        <CallFeedbackField
                          key={call.call_id}
                          callId={call.call_id}
                          initialFeedback={callDetail.call_feedback}
                        />
                        <TestCallFeedbackTimeline
                          testCall={Boolean(call.test_call)}
                          notes={callDetail.test_call_feedback}
                          onSeek={
                            hasRecording && !recordingBlocked
                              ? (seconds) => seekRef.current?.(seconds)
                              : undefined
                          }
                        />
                      </>
                    ) : (
                      <p className="text-destructive text-sm">
                        Failed to load call feedback.
                      </p>
                    )
                  }
                />
              )}

              {call.key_points && (
                <SheetSectionCard
                  title="Key Points"
                  body={
                    <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono bg-gray-50 p-4 rounded-md border border-gray-200 max-h-[30vh] overflow-y-auto">
                      {call.key_points}
                    </pre>
                  }
                />
              )}

              <SheetSectionCard
                title={factsTitle}
                body={
                  <CallLogsSheetFacts
                    facts={callDetail?.facts ?? null}
                    isLoading={isCallDetailLoading}
                    error={callDetailError}
                    onRetry={refetchCallDetail}
                  />
                }
              />

              {(call.computer_use || computerUseSessions.length > 0) && (
                <SheetSectionCard
                  title="Computer Use"
                  body={
                    <>
                      {call.computer_use && (
                        <p className="mb-2 text-sm whitespace-pre-wrap leading-relaxed">
                          {call.computer_use}
                        </p>
                      )}
                      {computerUseSessions.map((session) => (
                        <Button
                          key={session.id}
                          variant="outline"
                          size="sm"
                          className="w-full justify-start gap-2 mb-1"
                          onClick={() => toTask(`/sessions/${session.id}`)}>
                          <Monitor className="h-4 w-4 shrink-0" />
                          <span className="truncate">{session.slug}</span>
                          <span className="text-muted-foreground ml-auto text-xs capitalize">
                            {session.status}
                          </span>
                        </Button>
                      ))}
                    </>
                  }
                />
              )}

              {/* Encounter Note — admin only */}
              {isGlobalAdmin && call.encounter_note && (
                <SheetSectionCard
                  title="Encounter Note"
                  body={
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">
                      {call.encounter_note}
                    </p>
                  }
                />
              )}

              {showWaypoints &&
                call.agent_context?.waypoints &&
                call.agent_context.waypoints.length > 0 && (
                  <SheetSectionCard
                    title={
                      <span className="flex items-center gap-1.5">
                        Waypoints
                        <AccessLock />
                      </span>
                    }
                    body={
                      <WaypointRow
                        waypoints={call.agent_context.waypoints}
                        className="px-0 py-0"
                      />
                    }
                  />
                )}

              {/* Rubric analysis — evaluation_rubrics orgs, by
                  calls.rubricAnalysis role */}
              {rubricAnalysis.visible && (
                <SheetSectionCard
                  // RubricAnalysisPanel renders its own title row so the
                  // copy action can sit on the same line, top-right.
                  body={
                    <RubricAnalysisPanel
                      call={call}
                      staffOnly={rubricAnalysis.staffOnly}
                    />
                  }
                />
              )}

              {/* Copy a shareable link to this call — available to all users */}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleCopyCallLink}>
                <Link className="h-4 w-4 mr-2" />
                Copy link
              </Button>

              {/* Open full detail — global admins only */}
              {isGlobalAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full relative"
                  // No selection reset here: this tab's URL sync would write
                  // `/calls` back over the navigation. The sheet hides with
                  // its tab.
                  onClick={() => toTask(`/calls/${call.call_id}`)}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open full detail
                  <Lock
                    aria-label="Restricted to global admins"
                    className="absolute right-1"
                  />
                </Button>
              )}
            </div>
          ) : isLoading ? (
            <CallLogsSheetBodySkeleton />
          ) : (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Select a row to view details
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
